import {
  MONITOR_STATUS_LABELS,
  type AssetStatusEvent,
  type MonitoredDevice,
  type Paginated,
} from '@ftthnms/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io } from 'socket.io-client';
import { useToast } from '../components/Toast';

interface FeatureCollectionLike {
  features: Array<{ properties: { id: string; code?: string; name?: string; mon?: string | null } }>;
}

const RealtimeContext = createContext(false);

/**
 * One Socket.IO connection per tab (MON-03). The server puts it in the rooms of the user's areas.
 * Status events patch the cached map features and device list in place; status changes also refresh
 * summaries and show a toast for outages and recoveries.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [connected, setConnected] = useState(false);
  // Outages and recoveries arriving together (one scheduler tick) become one toast each.
  const pending = useRef<{ down: string[]; up: string[]; timer?: ReturnType<typeof setTimeout> }>({
    down: [],
    up: [],
  });

  useEffect(() => {
    const socket = io({ path: '/socket.io', transports: ['websocket'], withCredentials: true });
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('asset.status', (e: AssetStatusEvent) => {
      let label = '';
      qc.setQueriesData<FeatureCollectionLike>({ queryKey: ['map-features'] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          features: old.features.map((f) => {
            if (f.properties.id !== e.assetId) return f;
            label = f.properties.code ?? '';
            return { ...f, properties: { ...f.properties, mon: e.status } };
          }),
        };
      });
      qc.setQueriesData<Paginated<MonitoredDevice>>({ queryKey: ['monitoring', 'devices'] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((d) => {
            if (d.assetId !== e.assetId) return d;
            label ||= d.code;
            return {
              ...d,
              status: e.status,
              latencyMs: e.latencyMs,
              lossPct: e.lossPct,
              lastCheckedAt: e.checkedAt,
              since: e.changed ? e.checkedAt : d.since,
            };
          }),
        };
      });
      void qc.invalidateQueries({ queryKey: ['asset', e.assetId, 'monitoring'] });
      if (!e.changed) return;
      for (const key of [
        ['monitoring', 'summary'],
        ['monitoring', 'events'],
        ['assets'],
        ['customers'],
        ['dashboard'],
      ])
        void qc.invalidateQueries({ queryKey: key });
      const p = pending.current;
      if (e.status === 'offline') p.down.push(label || 'Perangkat');
      else if (e.status === 'online' && e.previous === 'offline') p.up.push(label || 'Perangkat');
      else return;
      clearTimeout(p.timer);
      p.timer = setTimeout(() => {
        const list = (names: string[]) =>
          names.length > 4 ? `${names.slice(0, 4).join(', ')} +${names.length - 4}` : names.join(', ');
        if (p.down.length)
          toast({
            tone: 'danger',
            text:
              p.down.length === 1
                ? `${p.down[0]} OFFLINE`
                : `${p.down.length} perangkat OFFLINE: ${list(p.down)}`,
          });
        if (p.up.length)
          toast({
            tone: 'success',
            text: `${list(p.up)} kembali ${MONITOR_STATUS_LABELS.online.toLowerCase()}`,
          });
        p.down = [];
        p.up = [];
      }, 1500);
    });
    socket.on('incident.changed', () => {
      for (const key of [['incidents'], ['dashboard']]) void qc.invalidateQueries({ queryKey: key });
    });
    return () => {
      socket.disconnect();
    };
  }, [qc, toast]);

  return <RealtimeContext.Provider value={connected}>{children}</RealtimeContext.Provider>;
}

/** Whether the realtime channel is connected (for the "Live" badge). */
export const useRealtimeConnected = () => useContext(RealtimeContext);
